import { Component, computed, effect, input, model, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DeepSignal } from '@ngrx/signals';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { createPageArray, HlmPaginationImports } from '@spartan-ng/helm/pagination';
import { HlmSelectImports } from '@spartan-ng/helm/select';

@Component({
  selector: 'app-paginateur',
  imports: [FormsModule, BrnSelectImports, HlmSelectImports, HlmPaginationImports],
  template: `
    <nav hlmPagination>
      <ul hlmPaginationContent>
        @if (!isFirstPage()) {
          <li hlmPaginationItem>
            <hlm-pagination-previous (click)="goToPrevious()" iconOnly="true" />
          </li>
        }
        @for (page of pages(); track page) {
          <li hlmPaginationItem>
            @if (page === '...') {
              <hlm-pagination-ellipsis />
            } @else {
              <a [isActive]="currentPageModel() === page" (click)="onPageClick(page)" hlmPaginationLink>
                {{ page }}
              </a>
            }
          </li>
        }
        @if (!isLastPage()) {
          <li hlmPaginationItem>
            <hlm-pagination-next (click)="goToNext()" iconOnly="true" />
          </li>
        }
      </ul>
    </nav>
  `,
})
export class PaginateurComponent {
  readonly store = input.required<{
    setPageNumber: (pageNb: number) => void;
    totalPageNb: Signal<number>;
    paginator: DeepSignal<{ pageNb: number; pageSize: number }>;
    firstItemNumber: Signal<number>;
    lastItemNumber: Signal<number>;
    count: Signal<number>;
  }>();

  // Models for HlmNumberedPagination - these bridge the store interface
  readonly currentPageModel = model<number>(1);
  readonly itemsPerPage = computed(() => this.store().paginator().pageSize);

  // Computed properties for pagination logic
  readonly totalPages = computed(() => Math.max(Math.ceil(this.store().count() / this.itemsPerPage()), 1));
  readonly isFirstPage = computed(() => this.currentPageModel() === 1);
  readonly isLastPage = computed(() => this.currentPageModel() === this.totalPages());

  readonly availablePages = computed(() => {
    const total = this.totalPages();
    return Array.from({ length: total }, (_, i) => i + 1);
  });
  readonly pages = computed(() => {
    return createPageArray(this.currentPageModel(), this.itemsPerPage(), this.store().count(), 7);
  });

  constructor() {
    // Initialize models and sync with store when available
    effect(() => {
      // This effect will run when store becomes available and whenever it changes
      const store = this.store();
      if (store) {
        const storePageNb = store.paginator.pageNb();

        // Initialize models if they haven't been set yet or update if different
        if (this.currentPageModel() !== storePageNb) {
          this.currentPageModel.set(storePageNb);
        }
      }
    });
  }

  // Navigation methods - these directly update the store
  goToPrevious(): void {
    if (!this.isFirstPage()) {
      const newPage = this.currentPageModel() - 1;
      this.store().setPageNumber(newPage);
    }
  }

  goToNext(): void {
    if (!this.isLastPage()) {
      const newPage = this.currentPageModel() + 1;
      this.store().setPageNumber(newPage);
    }
  }

  // Handle page link click
  onPageClick(page: number): void {
    this.store().setPageNumber(page);
  }
}
